<template>
  <div>
    <ParagraphBody>{{ introductionMessage }}</ParagraphBody>
    <ActionSelector
      :value="selectedOptionValue"
      @input="handleActionChange"
    />
    <GuideMessage
      caretPosition="left"
      :class="$style.actionGuide"
      name="action"
    />
    <SupportedMfa
      v-if="shouldShowSupportedMfaList"
      :allowedMfaTypes="allowedMfaTypes"
      :configs="configs"
      :isLoading="isLoadingMfaConfigs"
    />
    <NotificationInPage
      v-if="shouldShowErrorMessage"
      :notification="mfaError"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';

import AuthNPolicyType, { authnPolicyAllowedMfaTypes, authnPolicyType } from 'client/Security/AuthNPolicyType';

import { NotificationInPage, ParagraphBody, Types } from '@jumpcloud/ui-components';
import ActionSelector from 'client/Security/AuthNPoliciesAsidePanel/ActionSection/ActionSelector.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import SupportedMfa from 'client/Security/AuthNPoliciesAsidePanel/ActionSection/SupportedMfa.vue';

import { AuthnPolicyConsts } from 'client/util/Constants';

const { policyActions } = AuthnPolicyConsts;

const { NotificationConfig } = Types;

export default {
  name: 'ActionSection',

  components: {
    ActionSelector,
    GuideMessage,
    NotificationInPage,
    ParagraphBody,
    SupportedMfa,
  },

  props: {
    value: {
      type: Object,
      default: () => new AuthNPolicyType(),
    },
  },

  data() {
    return {
      isLoadingMfaConfigs: true,
      mfaError: NotificationConfig({
        icon: 'error',
        isMarkdown: true,
        message: '**At least one supported MFA method must be enabled to save this policy.**',
        type: 'error',
      }),
    };
  },

  computed: {
    ...mapState('PushMfaUserPortalModule', {
      userPortalPushConfig: 'config',
    }),

    ...mapState('DuoSettingsModule', [
      'userPortalDuoConfig',
    ]),

    ...mapState('TotpSettingsModule', [
      'userPortalTotpConfig',
    ]),

    ...mapState('WebAuthnUserPortalModule', {
      userPortalWebauthnConfig: 'config',
    }),

    allowedMfaTypes() {
      return authnPolicyAllowedMfaTypes[this.value.type];
    },

    configs() {
      return this.allowedMfaTypes.map((type) => {
        const config = this.getIndividualConfig(type);
        return {
          ...config,
          type,
        };
      });
    },

    introductionMessage() {
      return 'Determine how this policy affects user authentication. Conflicting policies are evaluated based on strictness.';
    },

    isLdapPolicy() {
      return this.value.type === authnPolicyType.ldap;
    },

    selectedOptionValue() {
      return this.value.action;
    },

    shouldShowErrorMessage() {
      const error = !this.configs.some(config => config.enabled);
      const showError = error && this.shouldShowSupportedMfaList;
      this.updateCanSaveItem(!showError);

      return showError;
    },

    shouldShowSupportedMfaList() {
      return this.value.action === policyActions.allowWithMFA;
    },
  },

  mounted() {
    // fetch mfa settings here to avoid refetching when changing options
    this.fetchMfaConfigs();
  },

  destroyed() {
    // reset canSaveItem on navigation from panel
    this.updateCanSaveItem(true);
  },

  methods: {
    ...mapActions('AuthNPolicyModel', [
      'updateCanSaveItem',
    ]),

    ...mapActions('DuoSettingsModule', {
      fetchDuoConfigs: 'getAllDuoSettings',
    }),

    ...mapActions('PushMfaUserPortalModule', {
      fetchPushConfigs: 'initialize',
    }),

    ...mapActions('TotpSettingsModule', {
      fetchTotpConfigs: 'initialize',
    }),

    ...mapActions('WebAuthnUserPortalModule', {
      fetchWebauthnConfigs: 'initialize',
    }),

    fetchIndividualType(type) {
      switch (type) {
        case 'duo':
          return this.fetchDuoConfigs();
        case 'push':
          return this.fetchPushConfigs();
        case 'totp':
          return this.fetchTotpConfigs();
        case 'webauthn':
          return this.fetchWebauthnConfigs();
        default:
          return null;
      }
    },

    async fetchMfaConfigs() {
      this.isLoadingMfaConfigs = true;
      const mfaConfigStatus = this.allowedMfaTypes.map(this.fetchIndividualType);

      await Promise.allSettled(mfaConfigStatus);

      this.isLoadingMfaConfigs = false;
    },

    getIndividualConfig(type) {
      switch (type) {
        case 'duo':
          return this.userPortalDuoConfig;
        case 'push':
          return this.userPortalPushConfig;
        case 'totp':
          return this.userPortalTotpConfig;
        case 'webauthn':
          return this.userPortalWebauthnConfig;
        default:
          return null;
      }
    },

    handleActionChange(value) {
      const isMFARequired = value === policyActions.allowWithMFA;

      this.$emit('input', {
        ...this.value,
        action: value,
        effect: {
          obligations: { mfa: { required: isMFARequired } },
        },
      });
    },
  },
};
</script>

<style module>
.actionGuide div {
  left: 200px;
  top: -102px;
}
</style>
